import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  Receipt,
  CreditCard,
  TrendingUp,
  DollarSign,
  Users,
  ShoppingBag,
  Scissors,
  Store,
  Sun,
  Printer,
  FileDown,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { SubPageHeader } from "../../components/ui/SubPageHeader";
import { useAuth } from "../../auth/AuthContext";
import { fetchZReportDetail, fetchZReportPdf, ZReportDetailResponse } from "../../services/reportsApi";
import { useToast } from "../../hooks/useToast";

interface TenderItem {
  method: string;
  in: number;
  out: number;
  total: number;
}

const StatCard = ({
  label,
  value,
  icon: Icon,
  subValue,
  showCurrency = true,
}: {
  label: string;
  value: string | number;
  icon: any;
  subValue?: string;
  highlight?: boolean;
  showCurrency?: boolean;
}) => {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";

  const displayValue =
    typeof value === "number"
      ? showCurrency
        ? `${currencySymbol}${value.toFixed(2)}`
        : value
      : value;

  return (
    <div className="p-5 rounded-2xl border border-gray-100 bg-white text-gray-900 shadow-sm">
      <div className="flex justify-between items-start mb-4">
        <div className="p-2 rounded-xl bg-purple-50">
          <Icon size={20} className="text-[#885ABB]" />
        </div>
        {subValue && (
          <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-50 text-green-600">
            {subValue}
          </span>
        )}
      </div>
      <div className="space-y-1">
        <h3 className="text-sm font-medium text-gray-500">{label}</h3>
        <p className="text-2xl font-bold tracking-tight">
          {displayValue}
        </p>
      </div>
    </div>
  );
};

const SectionHeader = ({
  title,
  icon: Icon,
}: {
  title: string;
  icon?: any;
}) => (
  <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-100">
    {Icon && <Icon size={20} className="text-[#885ABB]" />}
    <h2 className="text-lg font-bold text-gray-900">{title}</h2>
  </div>
);

const DetailRow = ({
  label,
  value,
  isTotal = false,
}: {
  label: string;
  value: string | number;
  isTotal?: boolean;
}) => {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";

  return (
    <div
      className={`flex justify-between items-center py-3 ${isTotal ? "border-t border-gray-100 mt-2 pt-4" : ""
        }`}
    >
      <span
        className={`${isTotal
          ? "text-base font-bold text-gray-900"
          : "text-sm font-medium text-gray-500"
          }`}
      >
        {label}
      </span>
      <span
        className={`${isTotal
          ? "text-lg font-bold text-[#885ABB]"
          : "text-sm font-bold text-gray-700"
          }`}
      >
        {typeof value === "number" ? `${currencySymbol}${value.toFixed(2)}` : value}
      </span>
    </div>
  );
};

const ZReportDetail: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<ZReportDetailResponse["report"] | null>(null);
  const [pdfLoading, setPdfLoading] = useState<"print" | "download" | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadReportDetail = async () => {
      if (!id) return;

      setIsLoading(true);
      setError(null);
      try {
        const response = await fetchZReportDetail(id);
        setData(response.report);
      } catch (err) {
        console.error("Failed to fetch Z report detail:", err);
        setError("Failed to load report details");
      } finally {
        setIsLoading(false);
      }
    };

    loadReportDetail();
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-red-500 text-lg">{error || "Report not found"}</p>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    );
  }

  // Parse tender data
  const tenderItems: TenderItem[] = [
    {
      method: "Cash",
      in: parseFloat(data.tenders.cashIn),
      out: parseFloat(data.tenders.cashOut),
      total: parseFloat(data.tenders.cashIn) - parseFloat(data.tenders.cashOut),
    },
    {
      method: "Card",
      in: parseFloat(data.tenders.cardIn),
      out: parseFloat(data.tenders.cardOut),
      total: parseFloat(data.tenders.cardIn) - parseFloat(data.tenders.cardOut),
    },
    {
      method: "Cheque",
      in: parseFloat(data.tenders.chequeIn),
      out: parseFloat(data.tenders.chequeOut),
      total: parseFloat(data.tenders.chequeIn) - parseFloat(data.tenders.chequeOut),
    },
  ];

  const companyInfo = data.company_info[0] || { name: "N/A", address: "N/A" };

  const handlePrint = async () => {
    if (!id) return;
    setPdfLoading("print");
    try {
      const blob = await fetchZReportPdf(id);
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (w) w.onload = () => URL.revokeObjectURL(url);
      else URL.revokeObjectURL(url);
      toast.success("Report opened in new tab. Use the browser print button to print.");
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? "Failed to load PDF";
      toast.error(msg);
    } finally {
      setPdfLoading(null);
    }
  };

  const handleDownloadPdf = async () => {
    if (!id) return;
    setPdfLoading("download");
    try {
      const blob = await fetchZReportPdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Z-Report-${data.close_out}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded.");
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? "Failed to download PDF";
      toast.error(msg);
    } finally {
      setPdfLoading(null);
    }
  };

  return (
    <div className="flex-1 flex flex-col space-y-6 lg:space-y-8 p-4 lg:p-6 bg-gray-50/50 min-h-screen">
      {/* Navigation Header */}
      <SubPageHeader
        title={`Z-Report #${data.close_out}`}
        subtitle="Comprehensive breakdown of daily closing report"
        icon={Receipt}
        actions={
          <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
            <Button
              variant="outline"
              size="sm"
              className="h-10 lg:h-9 bg-white hover:bg-gray-50 text-gray-700 border-gray-200 rounded-xl lg:rounded-lg font-bold lg:font-medium shadow-sm"
              onClick={handlePrint}
              disabled={!!pdfLoading}
            >
              <Printer size={16} className="mr-1.5 lg:mr-2" />
              <span>{pdfLoading === "print" ? "Opening…" : "Print"}</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-10 lg:h-9 bg-white hover:bg-gray-50 text-gray-700 border-gray-200 rounded-xl lg:rounded-lg font-bold lg:font-medium shadow-sm"
              onClick={handleDownloadPdf}
              disabled={!!pdfLoading}
            >
              <FileDown size={16} className="mr-1.5 lg:mr-2" />
              <span>{pdfLoading === "download" ? "Downloading…" : "Download PDF"}</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-10 lg:h-9 bg-white hover:bg-gray-50 text-gray-700 border-gray-200 rounded-xl lg:rounded-lg font-bold lg:font-medium shadow-sm"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={16} className="mr-1.5 lg:mr-2" />
              <span>Back</span>
            </Button>
          </div>
        }
      />

      {/* Top Stats Grid — Salonify: Net = total_sale_amount; Total Sales = net + tax */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
        <StatCard
          label="Total Net Sale"
          value={data.total_sale_amount}
          icon={TrendingUp}
          highlight
        />
        <StatCard
          label="Total Sales"
          value={data.total_sale_amount + data.total_tax_amount}
          icon={DollarSign}
        />
        <div className="hidden sm:block lg:block">
          <StatCard
            label="Total Tax"
            value={data.total_tax_amount}
            icon={Receipt}
          />
        </div>
        <div className="hidden sm:block lg:block">
          <StatCard
            label="Quick Payments"
            value={parseFloat(String(data.total_quickpayments).replace(/,/g, "")) || 0}
            icon={CreditCard}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {/* Left Column: Main Detailed Tables */}
        <div className="lg:col-span-2 space-y-6 lg:space-y-8">
          {/* Cash Up Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:p-6">
            <SectionHeader title="Check Up & Tender" icon={CreditCard} />

            <div className="overflow-hidden rounded-xl border border-gray-100">
              <div className="hidden sm:grid grid-cols-4 bg-gray-50 p-4 text-[10px] lg:text-xs font-bold text-gray-500 uppercase tracking-wider">
                <div>Type</div>
                <div className="text-right">In</div>
                <div className="text-right">Out</div>
                <div className="text-right">Total</div>
              </div>
              <div className="divide-y divide-gray-100">
                {tenderItems.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex flex-col sm:grid sm:grid-cols-4 p-4 hover:bg-purple-50/10 transition-colors gap-3 sm:gap-0"
                  >
                    <div className="font-bold sm:font-medium text-gray-900 flex items-center gap-2">
                      <div
                        className={`w-2 h-2 rounded-full ${item.total > 0 ? "bg-green-500" : "bg-gray-300"
                          }`}
                      />
                      {item.method}
                    </div>
                    <div className="flex justify-between sm:block text-right">
                      <span className="sm:hidden text-[10px] font-bold text-gray-400 uppercase">In</span>
                      <span className="text-gray-600 font-mono text-sm">{currencySymbol}{item.in.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between sm:block text-right">
                      <span className="sm:hidden text-[10px] font-bold text-gray-400 uppercase">Out</span>
                      <span className="text-gray-600 font-mono text-sm">{currencySymbol}{item.out.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between sm:block text-right pt-2 sm:pt-0 border-t sm:border-0 border-gray-50">
                      <span className="sm:hidden text-[10px] font-bold text-gray-400 uppercase">Total</span>
                      <span className="font-black sm:font-bold text-[#885ABB] sm:text-gray-900 font-mono text-base sm:text-sm">{currencySymbol}{item.total.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-[#402B69] sm:bg-gray-50 p-5 lg:p-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="font-black text-white sm:text-gray-900 text-sm lg:text-base uppercase tracking-tight sm:normal-case sm:tracking-normal">
                    Total Tendered
                  </span>
                  <span className="text-xl lg:text-2xl font-black text-white sm:text-[#885ABB]">
                    {currencySymbol}{parseFloat(data.tenders.totalTenderAmount).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Breakdown Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Sales by Service */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:p-6 flex flex-col">
              <SectionHeader title="Sales by Service" icon={Scissors} />
              <div className="space-y-4 flex-1">
                {data.by_services.length > 0 ? (
                  data.by_services.map((item, idx) => {
                    const canceled = String(item.status || "").toLowerCase() === "canceled";
                    const amount = typeof item.amount === "number"
                      ? item.amount
                      : parseFloat(String(item.amount).replace(/,/g, "")) || 0;
                    return (
                      <div
                        key={idx}
                        className="flex justify-between items-start group gap-4"
                      >
                        <span className={`text-sm font-medium leading-relaxed ${canceled ? "text-gray-400 line-through" : "text-gray-600 group-hover:text-[#885ABB]"}`}>
                          {item.name}
                          {canceled && (
                            <span className="ml-2 text-[10px] uppercase text-red-500 font-bold no-underline">Canceled</span>
                          )}
                        </span>
                        <span className={`text-sm font-bold whitespace-nowrap ${canceled ? "text-gray-400" : "text-gray-900"}`}>
                          {canceled ? "- " : ""}{currencySymbol}{amount.toFixed(2)}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-gray-400 italic">No service sales</p>
                )}
              </div>
              <div className="mt-6 space-y-1">
                <DetailRow
                  label="Total Net"
                  value={data.services_total_amount}
                  isTotal
                />
                <DetailRow
                  label="Total"
                  value={(data.by_services || []).reduce((sum, item) => {
                    if (String(item.status || "").toLowerCase() === "canceled") return sum;
                    const amount = typeof item.amount === "number"
                      ? item.amount
                      : parseFloat(String(item.amount).replace(/,/g, "")) || 0;
                    return sum + amount;
                  }, 0)}
                />
              </div>
            </div>

            {/* Sales by Product */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:p-6 flex flex-col">
              <SectionHeader title="Sales by Product" icon={ShoppingBag} />
              <div className="space-y-4 flex-1">
                {data.by_products.length > 0 ? (
                  data.by_products.map((item, idx) => {
                    const canceled = String(item.status || "").toLowerCase() === "canceled";
                    const amount = typeof item.amount === "number"
                      ? item.amount
                      : parseFloat(String(item.amount).replace(/,/g, "")) || 0;
                    return (
                      <div
                        key={idx}
                        className="flex justify-between items-start group gap-4"
                      >
                        <span className={`text-sm font-medium leading-relaxed ${canceled ? "text-gray-400 line-through" : "text-gray-600 group-hover:text-[#885ABB]"}`}>
                          {item.name}
                          {canceled && (
                            <span className="ml-2 text-[10px] uppercase text-red-500 font-bold no-underline">Canceled</span>
                          )}
                        </span>
                        <span className={`text-sm font-bold whitespace-nowrap ${canceled ? "text-gray-400" : "text-gray-900"}`}>
                          {canceled ? "- " : ""}{currencySymbol}{amount.toFixed(2)}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-gray-400 italic">No product sales</p>
                )}
              </div>
              <div className="mt-6 space-y-1">
                <DetailRow
                  label="Total Net"
                  value={data.products_total_amount}
                  isTotal
                />
                <DetailRow
                  label="Total"
                  value={(data.by_products || []).reduce((sum, item) => {
                    if (String(item.status || "").toLowerCase() === "canceled") return sum;
                    const amount = typeof item.amount === "number"
                      ? item.amount
                      : parseFloat(String(item.amount).replace(/,/g, "")) || 0;
                    return sum + amount;
                  }, 0)}
                />
              </div>
            </div>
          </div>

          {/* Sales by Employee */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:p-6 flex flex-col">
            <SectionHeader title="Sales by Employee" icon={Users} />
            <div className="space-y-4 flex-1">
              {(data.by_employee || []).map((item, idx) => {
                const amount = typeof item.amount === "number"
                  ? item.amount
                  : parseFloat(String(item.amount).replace(/,/g, "")) || 0;
                const totalSales = Number(item.total_sales) || 0;
                return (
                  <div key={idx} className="flex justify-between items-center group">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-[10px] font-black text-[#885ABB] shadow-sm transform group-hover:scale-110 transition-transform">
                        {item.name.charAt(0)}
                      </div>
                      <span className="text-sm font-bold text-gray-700 group-hover:text-[#885ABB] transition-colors">
                        {item.name}
                      </span>
                    </div>
                    <span className="text-sm font-black text-gray-900">
                      {currencySymbol}{amount.toFixed(2)}
                      {totalSales > 0 && (
                        <span className="ml-1 text-xs font-medium text-gray-400">(x{totalSales})</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="mt-6">
              <DetailRow
                label="Total"
                value={(data.by_employee || []).reduce((sum, item) => {
                  const amount = typeof item.amount === "number"
                    ? item.amount
                    : parseFloat(String(item.amount).replace(/,/g, "")) || 0;
                  return sum + amount;
                }, 0)}
                isTotal
              />
            </div>
          </div>

          {/* Commissions by Employee */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:p-6 flex flex-col">
            <SectionHeader title="Commissions by Employee" icon={DollarSign} />
            <div className="space-y-4 flex-1">
              {(data.by_employee || []).length > 0 ? (
                (data.by_employee || []).map((item, idx) => {
                  const commission = typeof item.commission === "number"
                    ? item.commission
                    : parseFloat(String(item.commission ?? "0").replace(/,/g, "")) || 0;
                  return (
                    <div key={idx} className="flex justify-between items-center group">
                      <span className="text-sm font-bold text-gray-700 group-hover:text-[#885ABB] transition-colors">
                        {item.name}
                      </span>
                      <span className="text-sm font-black text-gray-900">
                        {currencySymbol}{commission.toFixed(2)}
                      </span>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-gray-400 italic">No commissions</p>
              )}
            </div>
            <div className="mt-6">
              <DetailRow
                label="Total"
                value={(data.by_employee || []).reduce((sum, item) => {
                  const commission = typeof item.commission === "number"
                    ? item.commission
                    : parseFloat(String(item.commission ?? "0").replace(/,/g, "")) || 0;
                  return sum + commission;
                }, 0)}
                isTotal
              />
            </div>
          </div>

          {/* Sales by Sunbed */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:p-6 flex flex-col">
            <SectionHeader title="Sales by Sunbed" icon={Sun} />
            <div className="space-y-4 flex-1">
              {(data.by_sunbed && data.by_sunbed.length > 0) ? (
                data.by_sunbed.map((item, idx) => {
                  const canceled = String(item.status || "").toLowerCase() === "canceled";
                  const amount = typeof item.amount === "number"
                    ? item.amount
                    : parseFloat(String(item.amount).replace(/,/g, "")) || 0;
                  return (
                    <div key={idx} className="flex justify-between items-center group">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-[10px] font-black text-amber-600 shadow-sm transform group-hover:scale-110 transition-transform">
                          <Sun size={14} />
                        </div>
                        <span className={`text-sm font-bold ${canceled ? "text-gray-400 line-through" : "text-gray-700 group-hover:text-[#885ABB]"}`}>
                          {item.name}
                          {canceled && (
                            <span className="ml-2 text-[10px] uppercase text-red-500 font-bold no-underline">Canceled</span>
                          )}
                        </span>
                      </div>
                      <span className={`text-sm font-black ${canceled ? "text-gray-400" : "text-gray-900"}`}>
                        {canceled ? "- " : ""}{currencySymbol}{amount.toFixed(2)}
                      </span>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-gray-400 italic">No sunbed sales</p>
              )}
            </div>
            {data.sunbed_total_amount != null && data.sunbed_total_amount > 0 && (
              <div className="mt-6">
                <DetailRow
                  label="Sunbed Total"
                  value={data.sunbed_total_amount}
                  isTotal
                />
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Info & Summary */}
        <div className="lg:col-span-1 space-y-6">
          {/* Business Profile Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-0 overflow-hidden">
            <div className="bg-white p-6 border-b-2 border-b-[#885ABB]">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2.5 bg-purple-50 rounded-xl shadow-sm">
                  <Store className="text-[#885ABB]" size={24} />
                </div>
                <h2 className="text-xl font-black text-[#402B69] tracking-tight">{companyInfo.name}</h2>
              </div>
              <p className="text-gray-500 text-xs lg:text-sm flex items-start gap-2 pl-1 lg:pl-0">
                <MapPin size={16} className="text-[#885ABB] shrink-0 mt-0.5" />
                <span className="font-medium">{companyInfo.address}</span>
              </p>
            </div>

            <div className="px-6 pt-6">
              <div className="bg-purple-50/50 rounded-2xl border border-purple-100 p-5 space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-dashed border-purple-100/50">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider">
                    Start Time
                  </span>
                  <span className="text-sm font-bold text-[#402B69]">
                    {data.begin}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider">
                    End Time
                  </span>
                  <span className="text-sm font-bold text-[#402B69]">
                    {data.end}
                  </span>
                </div>
              </div>
            </div>

            <div className="px-6 pb-6 pt-2 space-y-1">
              <DetailRow
                label="Quick Payments"
                value={parseFloat(String(data.total_quickpayments).replace(/,/g, "")) || 0}
              />
            </div>
          </div>

          {/* Financial Summary */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-xs font-black text-[#402B69] uppercase tracking-widest mb-6 flex items-center gap-2">
              <div className="w-1.5 h-4 bg-[#885ABB] rounded-full" />
              Financial Summary
            </h3>
            <div className="space-y-1">
              <DetailRow
                label="Total Discount"
                value={data.total_discount}
              />
              <DetailRow
                label="Total Canceled"
                value={data.total_canceled_amount}
              />
              <DetailRow
                label="Canceled Tax"
                value={data.total_canceled_tax_amount}
              />
              <DetailRow
                label="Services Total"
                value={data.services_total_amount}
              />
              <DetailRow
                label="Products Total"
                value={data.products_total_amount}
              />
              {data.sunbed_total_amount != null && (
                <DetailRow
                  label="Sunbed Total"
                  value={data.sunbed_total_amount}
                />
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-100">
              <div className="flex justify-between items-end mb-3">
                <span className="text-sm font-medium text-gray-500">
                  Total Net Sale
                </span>
                <span className="text-lg font-bold text-gray-900">
                  {currencySymbol}{data.total_sale_amount.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-end mb-5">
                <span className="text-sm font-medium text-gray-500">Tax</span>
                <span className="text-lg font-bold text-gray-900">
                  {currencySymbol}{data.total_tax_amount.toFixed(2)}
                </span>
              </div>
              <div className="p-5 bg-[#402B69] rounded-2xl shadow-lg shadow-purple-900/10 flex flex-col gap-1">
                <span className="text-[10px] font-black text-purple-200 uppercase tracking-widest">Total Sales</span>
                <span className="text-3xl font-black text-white">
                  {currencySymbol}{(data.total_sale_amount + data.total_tax_amount).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZReportDetail;
